// @ts-nocheck
import React, { useContext } from "react";
import { HotelContext } from "context/HotelContext";
import { getLocale, translate } from "utils/Translation";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import Translation from "utils/Translation";

const ACTIVE_BAR_FILL = "#FAA54B";
const PENDING_BAR_FILL = "#DBDBDB";
const PENDING_BAR_HEIGHT = 48;
const PENDING_LABEL_WIDTH = 72;
const PENDING_LABEL_PADDING_Y = 5;
const PENDING_LABEL_LINE_HEIGHT = 9;
const PENDING_LABEL_FONT_SIZE = 8;

const getPendingMessageLines = (lang) => [
  translate("v2_pending_data_line1", lang),
  translate("v2_pending_data_line2", lang),
  translate("v2_pending_data_line3", lang),
];

const MonetaryTooltip = ({ active, payload }) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);

  const point = payload?.[0]?.payload;
  const isPending = Boolean(point?.isPlatformMonthPending);

  if (!active || !payload?.length || point?.isFutureMonth || isPending) {
    return null;
  }

  const value = Number(payload[0]?.value) || 0;
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid rgba(27, 36, 27, 0.15)",
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 13,
        color: "#1e3529",
      }}
    >
      {`${value.toLocaleString(locale)} €`}
    </div>
  );
};

const MonetaryValueLabel = (props) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const { x, y, width, value, payload } = props;

  const isPending = Boolean(payload?.isPlatformMonthPending);

  if (!width || x === undefined || y === undefined || payload?.isFutureMonth || isPending) {
    return null;
  }

  const numericValue = Number(value) || 0;
  if (numericValue <= 0) return null;

  return (
    <text x={x + width / 2} y={y - 8} textAnchor="middle" fill={ACTIVE_BAR_FILL} fontSize={14} fontWeight={700}>
      {`${Math.round(numericValue).toLocaleString(locale)}€`}
    </text>
  );
};

const MonetarySavingsBar = (props) => {
  const { lang } = useContext(HotelContext);
  const pendingMessageLines = getPendingMessageLines(lang);
  const { x, y, width, height, payload, background } = props;
  const isPending = Boolean(payload?.isPlatformMonthPending);

  if (payload?.isFutureMonth) {
    return null;
  }

  if (isPending) {
    const chartBottom = background ? background.y + background.height : y + height;
    const pendingY = chartBottom - PENDING_BAR_HEIGHT;
    const labelBlockHeight = PENDING_LABEL_PADDING_Y * 2 + pendingMessageLines.length * PENDING_LABEL_LINE_HEIGHT;
    const labelTop = pendingY - labelBlockHeight - 4;
    const centerX = x + width / 2;
    const labelLeft = centerX - PENDING_LABEL_WIDTH / 2;

    return (
      <g>
        <rect x={labelLeft} y={labelTop} width={PENDING_LABEL_WIDTH} height={labelBlockHeight} fill="#F3F3F3" rx={5} />
        {pendingMessageLines.map((line, index) => (
          <text
            key={line}
            x={centerX}
            y={labelTop + PENDING_LABEL_PADDING_Y + PENDING_LABEL_FONT_SIZE + index * PENDING_LABEL_LINE_HEIGHT}
            textAnchor="middle"
            fill="#393939"
            fontFamily="Montserrat, sans-serif"
            fontSize={PENDING_LABEL_FONT_SIZE}
            fontStyle="italic"
            fontWeight={400}
          >
            {line}
          </text>
        ))}
        <rect x={x} y={pendingY} width={width} height={PENDING_BAR_HEIGHT} fill={PENDING_BAR_FILL} rx={2} />
      </g>
    );
  }

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={ACTIVE_BAR_FILL} rx={2} />
    </g>
  );
};

const LineChartComponent_simple = ({ data, luniShowerOnly = false, hasWaterOption = true }) => {
  const { lang } = useContext(HotelContext);

  const computedData = (data || []).map((d) => {
    const isFutureMonth = Boolean(d.isFutureMonth);
    const hasMonthlyRecord = Boolean(d.hasMonthlyRecord);
    const showOnPlatform = Number(d.showOnPlatform) || 0;
    const isPlatformMonthPending = hasWaterOption && hasMonthlyRecord && showOnPlatform !== 1;
    const totalMonetarySavings =
      isFutureMonth || (hasWaterOption && (isPlatformMonthPending || !hasMonthlyRecord))
        ? 0
        : luniShowerOnly
          ? (Number(d.totalLuniShowerSavedInEuros) || 0)
          : (Number(d.totalEnergySavedInEuros) || 0) +
            (Number(d.totalWaterSavedInEuros) || 0) +
            (Number(d.totalActionsSavedInEuros) || 0);

    return {
      ...d,
      isFutureMonth,
      hasMonthlyRecord,
      showOnPlatform,
      isPlatformMonthPending,
      totalMonetarySavings,
    };
  });

  if (!computedData || computedData.length === 0) {
    return <p>{translate("v2_chart_no_data", lang)}</p>;
  }

  return (
    <div style={{ width: "100%", height: "350px" }}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={computedData} margin={{ top: 58, right: 30, left: 10, bottom: 0 }}>
          <XAxis dataKey="month" tick={{ fontSize: 14 }} textAnchor="middle" interval={0} />
          <YAxis hide />
          <Tooltip content={<MonetaryTooltip />} />
          <Bar
            dataKey="totalMonetarySavings"
            fill={ACTIVE_BAR_FILL}
            name={<Translation tag={"money_savings"} />}
            isAnimationActive={false}
            barSize={45}
            shape={(p) => <MonetarySavingsBar {...p} />}
          >
            <LabelList dataKey="totalMonetarySavings" content={MonetaryValueLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LineChartComponent_simple;
